// Journey templates that mirror the automations a Shopify merchant already runs in
// Shopify Email (welcome, abandoned checkout/cart, browse abandonment, post-purchase,
// first-purchase upsell, win-back) plus a wholesale reorder reminder for B2B tenants.
// Shopify Email exposes no API, so "Bring your email over" RECREATES these as Plunk
// draft journeys (spec: PlunkDocs/specs/SETUP_BRING_YOUR_EMAIL_OVER_2026-09-26.md).
// Each template builds the same graph + section designs the journey editor uses
// (common.js insertNode, composer starterDesign); the email bodies are rendered by
// the server's /campaign/email/render exactly like the editor's save path.
//
// Triggers use the engine's existing vocabulary (TriggerEvaluators): WelcomeSeries,
// ProductViewed, CartAbandoned, custom Engagement/Conversion events — the Shopify app
// pixel lands storefront events as engagements typed Product / Cart / Checkout and
// checkouts as conversions — and CustomerLapsed (win-back, wholesale reorder).

import { emptyGraph, insertNode } from "./common";
import { starterDesign } from "../email-automation/composer";
const design = sections => ({
  frame: "brand",
  version: 1,
  sections
});
function build(fn) {
  let g = emptyGraph();
  const designs = {};
  const add = (slot, type, patch, d) => {
    const r = insertNode(g, slot, type, patch);
    g = r.graph;
    if (d) designs[r.id] = d;
    return r.id;
  };
  const next = id => ({
    parentId: id,
    edge: "next"
  });
  fn(add, next);
  return {
    graph: g,
    designs
  };
}
export const SHOPIFY_EMAIL_TEMPLATES = [{
  key: "welcome",
  shopifyOwns: true,
  goal: "conversion",
  shopifyName: "Welcome new subscribers",
  name: "Welcome series",
  blurb: "New subscribers get a welcome, then a nudge toward what's popular. Enrolled once, ever.",
  steps: "Welcome → 3 days → Where to start",
  triggerType: "WelcomeSeries",
  triggerConfig: {
    eventName: "ContactCreated",
    eventCategory: null,
    lookbackDays: null,
    excludeConverted: true
  },
  audienceInclude: ["Email subscribers"],
  build: () => build((add, next) => {
    const e1 = add(null, "email", {
      name: "Welcome",
      emailSubject: "Welcome, {{FirstName}}"
    }, starterDesign("WelcomeSeries"));
    const d1 = add(next(e1), "delay", {
      delayDays: 3
    });
    add(next(d1), "email", {
      name: "Where to start",
      emailSubject: "A few places to start"
    }, design([{
      type: "hero",
      headline: "Where to start, {{FirstName}}",
      subhead: "A few favorites to get you going.",
      imageFrom: "none",
      label: "Explore the collection",
      url: ""
    }, {
      type: "recommended",
      title: "Popular right now",
      max: 3,
      columns: 3,
      showPrice: true
    }, {
      type: "button",
      label: "Shop new arrivals",
      url: "",
      align: "center"
    }]));
  })
}, {
  key: "abandoned_checkout",
  shopifyOwns: true,
  goal: "conversion",
  shopifyName: "Abandoned checkout",
  name: "Abandoned checkout",
  blurb: "Someone started checkout and didn't finish. Remind them, then offer a hand if they still haven't.",
  steps: "Reminder (1h+) → 1 day → converted? → offer",
  triggerType: "Engagement",
  triggerConfig: {
    eventName: "Checkout",
    eventCategory: "Engagement",
    lookbackDays: 2,
    excludeConverted: true
  },
  build: () => build((add, next) => {
    const e1 = add(null, "email", {
      name: "Finish checking out",
      emailSubject: "You left something at checkout, {{FirstName}}"
    }, design([{
      type: "hero",
      headline: "Your checkout is waiting, {{FirstName}}",
      subhead: "Everything is still here. Pick up where you left off.",
      imageFrom: "firstProduct",
      label: "Finish checking out",
      url: ""
    }, {
      type: "products",
      source: "trigger",
      max: 3,
      columns: 3,
      showPrice: true,
      button: "Back to checkout"
    }]));
    const d1 = add(next(e1), "delay", {
      delayDays: 1
    });
    const b = add(next(d1), "branch", {
      name: "Converted since?",
      condition: {
        kind: "converted",
        eventName: "",
        audienceId: "",
        windowDays: null
      }
    });
    add({
      parentId: b,
      edge: "yes"
    }, "exit");
    add({
      parentId: b,
      edge: "no"
    }, "email", {
      name: "A little help deciding",
      emailSubject: "Still deciding, {{FirstName}}?"
    }, design([{
      type: "hero",
      headline: "Still on your mind?",
      subhead: "Here's a little something to make the decision easier.",
      imageFrom: "firstProduct",
      label: "Take another look",
      url: ""
    }, {
      type: "products",
      source: "trigger",
      max: 3,
      columns: 3,
      showPrice: true,
      button: "View"
    }, {
      type: "offer",
      headline: "Free shipping on this order",
      html: "<p>Complete your order this week. Mention this email or use the code at checkout.</p>",
      code: "",
      label: "",
      url: ""
    }]));
  })
}, {
  key: "abandoned_cart",
  shopifyOwns: true,
  goal: "conversion",
  shopifyName: "Abandoned cart",
  name: "Cart recovery",
  blurb: "Added to cart, no order. A reminder with the items, then an offer.",
  steps: "Cart reminder → 1 day → converted? → offer",
  triggerType: "Engagement",
  triggerConfig: {
    eventName: "Cart",
    eventCategory: "Engagement",
    lookbackDays: 3,
    excludeConverted: true
  },
  build: () => build((add, next) => {
    const e1 = add(null, "email", {
      name: "Cart reminder",
      emailSubject: "Your cart is waiting, {{FirstName}}"
    }, starterDesign("CartAbandoned"));
    const d1 = add(next(e1), "delay", {
      delayDays: 1
    });
    const b = add(next(d1), "branch", {
      name: "Converted since?",
      condition: {
        kind: "converted",
        eventName: "",
        audienceId: "",
        windowDays: null
      }
    });
    add({
      parentId: b,
      edge: "yes"
    }, "exit");
    add({
      parentId: b,
      edge: "no"
    }, "email", {
      name: "An offer to help decide",
      emailSubject: "A little something to help you decide"
    }, design([{
      type: "hero",
      headline: "Still thinking it over, {{FirstName}}?",
      subhead: "Your cart is saved. Here's a little something to help.",
      imageFrom: "firstProduct",
      label: "Back to cart",
      url: ""
    }, {
      type: "products",
      source: "trigger",
      max: 3,
      columns: 3,
      showPrice: true,
      button: "Back to cart"
    }, {
      type: "offer",
      headline: "Free delivery this week",
      html: "<p>On your order this week. Mention this email or use the code at checkout.</p>",
      code: "",
      label: "",
      url: ""
    }]));
  })
}, {
  key: "browse",
  shopifyOwns: true,
  goal: "conversion",
  shopifyName: "Abandoned product browse",
  name: "Browse follow-up",
  blurb: "Looked at products, didn't buy. Bring them back to what they saw, plus what people like them bought.",
  steps: "Still thinking it over? → 1 day → converted? → picked for you",
  triggerType: "ProductViewed",
  triggerConfig: {
    eventName: "ProductViewed",
    eventCategory: null,
    lookbackDays: null,
    excludeConverted: true
  },
  build: () => build((add, next) => {
    const e1 = add(null, "email", {
      name: "Still thinking it over?",
      emailSubject: "Still thinking it over, {{FirstName}}?"
    }, starterDesign("ProductViewed"));
    const d1 = add(next(e1), "delay", {
      delayDays: 1
    });
    const b = add(next(d1), "branch", {
      name: "Converted since?",
      condition: {
        kind: "converted",
        eventName: "",
        audienceId: "",
        windowDays: null
      }
    });
    add({
      parentId: b,
      edge: "yes"
    }, "exit");
    add({
      parentId: b,
      edge: "no"
    }, "email", {
      name: "Picked for you",
      emailSubject: "A few things we think you'll like"
    }, design([{
      type: "hero",
      headline: "Picked for you, {{FirstName}}",
      subhead: "Based on what you've been looking at.",
      imageFrom: "firstProduct",
      label: "See them",
      url: ""
    }, {
      type: "recommended",
      title: "You might also like",
      max: 3,
      columns: 3,
      showPrice: true
    }]));
  })
}, {
  key: "post_purchase",
  shopifyOwns: true,
  shopifyName: "Post-purchase thank you",
  name: "Post-purchase thank you",
  blurb: "Every order gets a thank-you, and two weeks later a request for a review.",
  steps: "Thank you → 14 days → how did we do?",
  triggerType: "Conversion",
  triggerConfig: {
    eventName: "",
    eventCategory: "Conversion",
    lookbackDays: 1,
    excludeConverted: false
  },
  build: () => build((add, next) => {
    const e1 = add(null, "email", {
      name: "Thank you",
      emailSubject: "Thank you for your order, {{FirstName}}"
    }, design([{
      type: "hero",
      headline: "Thank you, {{FirstName}}",
      subhead: "Your order is on its way. Here's what happens next.",
      imageFrom: "none",
      label: "",
      url: ""
    }, {
      type: "text",
      html: "<p>We'll email you as soon as it ships. Questions in the meantime? Just reply to this email. A real person reads it.</p>"
    }]));
    const d1 = add(next(e1), "delay", {
      delayDays: 14
    });
    add(next(d1), "email", {
      name: "How did we do?",
      emailSubject: "How did we do, {{FirstName}}?"
    }, design([{
      type: "hero",
      headline: "How did we do?",
      subhead: "A quick review helps other customers, and helps us keep getting better.",
      imageFrom: "firstProduct",
      label: "Leave a review",
      url: ""
    }, {
      type: "recommended",
      title: "Goes well with your order",
      max: 3,
      columns: 3,
      showPrice: true
    }]));
  })
}, {
  key: "first_purchase_upsell",
  shopifyOwns: true,
  shopifyName: "First purchase upsell",
  name: "First-purchase follow-up",
  blurb: "A week after a purchase, recommendations based on what they bought.",
  steps: "7 days after purchase → recommendations",
  triggerType: "Conversion",
  triggerConfig: {
    eventName: "",
    eventCategory: "Conversion",
    lookbackDays: 1,
    excludeConverted: false
  },
  build: () => build((add, next) => {
    const d1 = add(null, "delay", {
      delayDays: 7
    });
    add(next(d1), "email", {
      name: "Goes well with it",
      emailSubject: "Goes well with your order, {{FirstName}}"
    }, design([{
      type: "hero",
      headline: "Picked to go with it, {{FirstName}}",
      subhead: "A few things that pair with what you chose.",
      imageFrom: "firstProduct",
      label: "See the picks",
      url: ""
    }, {
      type: "recommended",
      title: "Pairs well with your order",
      max: 3,
      columns: 3,
      showPrice: true
    }]));
  })
}, {
  key: "winback",
  shopifyOwns: true,
  goal: "conversion",
  shopifyName: "Win back lapsed customers",
  name: "Win-back",
  blurb: "Customers who haven't ordered in 90 days get a nudge, then an offer.",
  steps: "We miss you → 7 days → offer",
  triggerType: "CustomerLapsed",
  triggerConfig: {
    eventName: "retail",
    eventCategory: null,
    lookbackDays: 90,
    excludeConverted: true
  },
  build: () => build((add, next) => {
    const e1 = add(null, "email", {
      name: "We miss you",
      emailSubject: "It's been a while, {{FirstName}}"
    }, design([{
      type: "hero",
      headline: "It's been a while, {{FirstName}}",
      subhead: "Here's what's new since your last visit.",
      imageFrom: "none",
      label: "See what's new",
      url: ""
    }, {
      type: "recommended",
      title: "New since your last visit",
      max: 3,
      columns: 3,
      showPrice: true
    }]));
    const d1 = add(next(e1), "delay", {
      delayDays: 7
    });
    const b = add(next(d1), "branch", {
      name: "Converted since?",
      condition: {
        kind: "converted",
        eventName: "",
        audienceId: "",
        windowDays: null
      }
    });
    add({
      parentId: b,
      edge: "yes"
    }, "exit");
    add({
      parentId: b,
      edge: "no"
    }, "email", {
      name: "A welcome-back offer",
      emailSubject: "A welcome-back offer, {{FirstName}}"
    }, starterDesign("CustomerLoyalty"));
  })
}, {
  key: "wholesale_reorder",
  goal: "conversion",
  shopifyName: "Wholesale reorder reminder",
  name: "Wholesale reorder reminder",
  b2b: true,
  blurb: "Dealer and wholesale contacts get a reorder reminder 45 days after their last order.",
  steps: "45 days after last wholesale order → reorder reminder",
  triggerType: "CustomerLapsed",
  triggerConfig: {
    eventName: "wholesale",
    eventCategory: null,
    lookbackDays: 45,
    excludeConverted: true
  },
  audienceInclude: ["B2B contacts"],
  build: () => build(add => {
    add(null, "email", {
      name: "Time to reorder?",
      emailSubject: "Time to restock, {{FirstName}}?"
    }, design([{
      type: "hero",
      headline: "Time to restock, {{FirstName}}?",
      subhead: "It's been about six weeks since your last order. Here's what moved.",
      imageFrom: "none",
      label: "Reorder",
      url: ""
    }, {
      type: "recommended",
      title: "Best sellers this month",
      max: 3,
      columns: 3,
      showPrice: true
    }, {
      type: "text",
      html: "<p>Reply to this email or call your rep and we'll get it out this week.</p>"
    }]));
  })
}];
export function templatesFor({
  b2b
}) {
  return SHOPIFY_EMAIL_TEMPLATES.filter(t => !t.b2b || b2b);
}

// Positioning (2026-09-27): Shopify Email keeps the retail basics (shopifyOwns); Plunk adds what it
// cannot run. On a Shopify workspace only the Plunk-only templates are selected by default, and
// activating a shopifyOwns journey requires "I paused it in Shopify Email" (server-enforced).
export const SHOPIFY_OWNED_KEYS = SHOPIFY_EMAIL_TEMPLATES.filter(t => t.shopifyOwns).map(t => t.key);
export const SHOPIFY_OWNED_NAMES = SHOPIFY_EMAIL_TEMPLATES.filter(t => t.shopifyOwns).map(t => t.name.toLowerCase());
export function shopifyOwnsJourney(j) {
  if (!j) return false;
  if (j.templateKey) return SHOPIFY_OWNED_KEYS.includes(String(j.templateKey));
  return SHOPIFY_OWNED_NAMES.includes(String(j.name || "").trim().toLowerCase());
}
export function templateGroups({
  b2b
}) {
  const all = templatesFor({
    b2b
  });
  return {
    plunkOnly: all.filter(t => !t.shopifyOwns),
    shopifyOwns: all.filter(t => t.shopifyOwns)
  };
}